import { GQLProductFragment, GQLProductSideAvailableDecorationType } from '@theprintbar/graphql';
import { Merch, cloneDeep, cn, getSortedColours, getSortedSizes, merchQuantity, updateTeamNamesWithSize } from '@theprintbar/shared';
import { useCallback } from 'react';
import { Input, NumberInput } from '../FormFields';
import { LightningBolt } from '../Icons';
import { Ping } from '../Ping';
import { SizeGuideButton } from '../Product';

interface ProductSizesProps {
	merch?: Merch;
	loading?: boolean;
	product: GQLProductFragment;
	updateMerch: (merch: Merch) => void;
	freeEdit?: boolean;
	scrMinOrderQuantity?: number;
}

export function ProductSizes({ merch, product, loading, updateMerch, freeEdit, scrMinOrderQuantity }: ProductSizesProps) {
	const handleSizeChange = useCallback(
		(sizeCode: string, quantity: number) => {
			if (!merch) return;
			let newMerch = cloneDeep(merch);
			const index = newMerch.sizing.findIndex(({ size }) => size === sizeCode);

			if (index === -1) {
				if (!quantity) return;
				newMerch.sizing.push({ size: sizeCode, quantity });
			} else if (!!quantity) {
				newMerch.sizing[index].quantity = quantity;
			} else {
				newMerch.sizing.splice(index, 1);
			}
			updateMerch(newMerch);
		},
		[updateMerch, merch],
	);

	const colourCode = merch?.colour || getSortedColours(product.colours)[0].colour.code;
	const allSizes = getSortedSizes(product);
	const oneSize = allSizes.length === 1 && allSizes[0].variant.size.code === 'OS';
	const colourSizes = getSortedSizes(product, colourCode).filter(s => !merch?.sameday || s.sameday);
	const quantity = merchQuantity(merch);
	const hasScr = (merch?: Merch) => merch?.decorations?.some(d => d.type === GQLProductSideAvailableDecorationType.Scr);
	const minOrderQty = hasScr(merch) ? scrMinOrderQuantity : product.minOrder;
	const underMin = !!minOrderQty && !!quantity && quantity < minOrderQty;

	const sizeName = product.customSizeName || 'Size';

	return (
		<div className="space-y-2.5">
			<div className="mb-3 flex items-center space-x-4">
				<h5 className="text-xs uppercase">
					{sizeName} & Quantity
					{!!minOrderQty && <span className={cn('normal-case', underMin && 'text-red')}>- Min {minOrderQty}qty</span>}
				</h5>
				<SizeGuideButton product={product} />
			</div>
			<div className={cn('lg:w-3/4 flex gap-2 lg:gap-4 flex-wrap', loading && 'opacity-50 pointer-events-none')}>
				{allSizes.map(({ variant }, index) => {
					const currentQuantity = merch?.sizing.find(({ size }) => size === variant.size.code)?.quantity || 0;
					const size = colourSizes.find(s => s.variant.size.id === variant.size.id);
					const disabled = !size?.stocked || !freeEdit;

					if (!freeEdit && !currentQuantity) return null;

					return (
						<div
							key={variant.id}
							className={cn(
								'flex flex-col items-center overflow-hidden uppercase',
								disabled && 'opacity-50 border-grey pointer-events-none text-grey',
								underMin && 'border-red text-red',
							)}
						>
							<div className={cn('flex items-center', oneSize ? 'normal-case pr-2' : 'w-20', underMin && 'border-red')}>
								<Ping
									animate={false}
									colour={size?.stocked ? 'bg-green' : 'bg-grey'}
									pingClassName="duration-[5000ms]"
									dimensions="w-2 h-2"
								/>
								<span className="ml-2 text-base">{oneSize ? 'One Size' : variant.size.code}</span>
								{size?.sameday && <LightningBolt className="w-4 h-4 fill-blue" />}
							</div>
							<div
								className={cn(
									'flex h-full w-20 transition border border-blue rounded-full',
									underMin
										? 'bg-red text-white'
										: !!currentQuantity
										? 'bg-blue text-white'
										: 'hover:bg-blue-bright/20 focus:bg-blue-bright/20',
								)}
							>
								<Input
									disabled={disabled}
									outsideForm
									type="hidden"
									name={`items[${index}].size`}
									value={variant.size.code}
								/>
								<NumberInput
									hideLabel
									name={`items[${index}].quantity`}
									outsideForm
									disabled={disabled}
									noValidate
									defaultValue={currentQuantity}
									value={currentQuantity}
									min={0}
									max={size?.variant.supplierStock || variant.supplierStock}
									handleUpdate={newQuantity => handleSizeChange(variant.size.code, newQuantity)}
									className="w-full py-1"
								/>
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
}
